import React, { Fragment, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { isEmpty, uniqBy } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildMakeModelDefaultYear,
  buildMmyModelFamilyIdsPath,
  buildModelFamilyIdsModelYearsPath,
  VehicleModel,
  VehicleEntities,
  buildForSaleYearsPath,
} from 'client/data/models/vehicle-v2';
import {
  getFilteredModelYears,
  getModelYearsWithReview,
  getModelYearsForSale,
  getForSaleLinks,
} from 'site-modules/shared/utils/core-page/model-year-helpers';
import { AnimatedCollapse } from 'site-modules/shared/utils/collapse-utils';
import { SpeculationRule } from 'site-modules/shared/components/speculation-rule/speculation-rule';
import { YearLink } from './year-link/year-link';

import './other-years.scss';

const COLLAPSE_ID = 'other-years-collapse';
const YEAR_LIST_STYLE = 'years-list-grid list-unstyled m-0';
const COLLAPSE_BTN_GRID_SIZE = 0.7;
const YEARS_PER_LINE = { true: 4, false: 7 };

export function OtherYearsUI({
  vehicle = null,
  modelYears = null,
  forSaleYears = null,
  defaultYear = null,
  className = null,
  heading = null,
  headingClassName = 'heading-5 mb-0_75',
  renderEmptyHidden = false,
  isMobile = false,
  hasSpeculationRule = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef();

  function toggle() {
    setIsOpen(!isOpen);
  }

  const {
    year,
    model: { name: modelName, slug: modelSlug },
    make: { name: makeName, slug: makeSlug },
  } = vehicle;
  const filteredModelYears = useMemo(() => getFilteredModelYears(modelYears, modelName), [modelYears, modelName]);

  if (isEmpty(filteredModelYears)) {
    return null;
  }

  const allModelYears = getModelYearsWithReview(filteredModelYears, defaultYear);
  const yearsForSale = uniqBy(
    [...getForSaleLinks({ forSaleYears, makeSlug, modelSlug }), ...getModelYearsForSale(filteredModelYears)],
    'name'
  );

  const yearsPerLine = YEARS_PER_LINE[isMobile];

  const headerFirstLinks = allModelYears.slice(0, yearsPerLine);
  const collapsedFirstLinks = allModelYears.slice(yearsPerLine);
  const hasCollapse = allModelYears.length > yearsPerLine || !!yearsForSale.length;
  if (!renderEmptyHidden && allModelYears.length < 2) {
    return null;
  }

  const collapseHeaderColumns = {
    '--mobile-columns': `${headerFirstLinks.length}fr ${
      YEARS_PER_LINE.true - headerFirstLinks.length + COLLAPSE_BTN_GRID_SIZE
    }fr`,
    '--desktop-columns': `${headerFirstLinks.length}fr ${
      YEARS_PER_LINE.false - headerFirstLinks.length + COLLAPSE_BTN_GRID_SIZE
    }fr`,
  };

  return (
    <div
      className={classnames('years-lite-pills', className, { 'd-none': renderEmptyHidden && allModelYears.length < 2 })}
      data-tracking-parent="years-module"
      ref={containerRef}
    >
      {!!heading && <div className={headingClassName}>{heading}</div>}
      <div className="medium">
        <div
          className="collapse-header"
          style={{
            ...collapseHeaderColumns,
            display: 'grid',
            gridTemplateColumns: isMobile
              ? collapseHeaderColumns['--mobile-columns']
              : collapseHeaderColumns['--desktop-columns'],
          }}
        >
          <ul
            className="years-list-header list-unstyled"
            style={{
              margin: '-5px',
              padding: '5px',
              display: 'grid',
              gridTemplateColumns: `repeat(${headerFirstLinks.length}, 1fr)`,
            }}
          >
            {headerFirstLinks.map(({ name, url, noFollow }) => (
              <YearLink
                key={`year-${name}`}
                year={name}
                url={url}
                currentYear={year}
                skipCurrentYear
                trackingId="see_other_years_core"
                noFollow={noFollow}
              />
            ))}
          </ul>
          {hasCollapse && (
            <div className={classnames('d-flex', { 'justify-content-end': headerFirstLinks.length === yearsPerLine })}>
              <Button
                size="sm"
                color="link"
                onClick={toggle}
                className="collapse-year-btn small text-transform-none px-0 py-0_5 border-0"
                style={{ fontSize: '.75rem', textTransform: 'none', minWidth: '3rem', fontWeight: 'normal' }}
                data-tracking-id="see_other_years"
                aria-label={`${isOpen ? 'Less' : 'More'} ${makeName} ${modelName} model years`}
                aria-controls={COLLAPSE_ID}
                aria-expanded={isOpen}
              >
                {isOpen ? 'less' : 'more'}{' '}
                <i
                  className={classnames('icon size-9', { 'icon-arrow-down3': !isOpen, 'icon-arrow-up3': isOpen })}
                  aria-hidden
                />
              </Button>
            </div>
          )}
        </div>
        {hasCollapse && (
          <AnimatedCollapse isOpen={isOpen} className="overflow-hidden-focusable" id={COLLAPSE_ID}>
            {!!collapsedFirstLinks.length && (
              <div className="review-years wrapper-collapse-years">
                <ul className={classnames(YEAR_LIST_STYLE, 'mt-1_5')}>
                  {collapsedFirstLinks.map(({ name, url, noFollow }) => (
                    <YearLink
                      key={`year-${name}`}
                      year={name}
                      url={url}
                      currentYear={year}
                      skipCurrentYear
                      trackingId="see_other_years_core"
                      noFollow={noFollow}
                    />
                  ))}
                </ul>
              </div>
            )}
            {!!yearsForSale.length && (
              <Fragment>
                <div className="mt-1_5 mb-1 heading-6">
                  {makeName} {modelName} for Sale
                </div>
                <div className="wrapper-collapse-years">
                  <ul className={YEAR_LIST_STYLE}>
                    {yearsForSale.map(({ name, url, noFollow }) => (
                      <YearLink
                        key={`year-${name}`}
                        year={name}
                        url={url}
                        trackingId="see_other_years_srp"
                        noFollow={noFollow}
                      />
                    ))}
                  </ul>
                </div>
              </Fragment>
            )}
          </AnimatedCollapse>
        )}
      </div>

      {hasSpeculationRule && (
        <Fragment>
          <SpeculationRule
            observeRef={containerRef}
            selector=".years-lite-pills .years-list-header a"
            notSelector="[rel~=nofollow]"
          />
          <SpeculationRule
            observeRef={containerRef}
            action="prefetch"
            eagerness="moderate"
            selector=".years-lite-pills .years-list-header a[rel~=nofollow], .years-lite-pills .wrapper-collapse-years.review-years a"
          />
        </Fragment>
      )}
    </div>
  );
}

OtherYearsUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  modelYears: PropTypes.arrayOf(VehicleEntities.ModelYearByFamilyId),
  forSaleYears: PropTypes.arrayOf(PropTypes.number),
  defaultYear: PropTypes.number,
  className: PropTypes.string,
  heading: PropTypes.string,
  headingClassName: PropTypes.string,
  renderEmptyHidden: PropTypes.bool,
  isMobile: PropTypes.bool,
  hasSpeculationRule: PropTypes.bool,
};

const OtherYearsWrapper = connectToModel(OtherYearsUI, {
  defaultYear: bindToPath(
    ({ vehicle }) => buildMakeModelDefaultYear({ make: vehicle.make.slug, model: vehicle.model.slug }),
    VehicleModel
  ),
  modelYears: bindToPath(
    ({ modelFamilyIds }) => !isEmpty(modelFamilyIds) && buildModelFamilyIdsModelYearsPath(modelFamilyIds),
    VehicleModel
  ),
});

export const OtherYears = connectToModel(OtherYearsWrapper, {
  modelFamilyIds: bindToPath(
    ({ vehicle }) =>
      buildMmyModelFamilyIdsPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
    VehicleModel
  ),
  forSaleYears: bindToPath(
    ({ vehicle }) => buildForSaleYearsPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
    VehicleModel
  ),
});

export const OtherYearsGraphql = connectToModel(OtherYearsUI, {
  defaultYear: bindToPath(
    ({ vehicle }) => buildMakeModelDefaultYear({ make: vehicle.make.slug, model: vehicle.model.slug }),
    VehicleModel
  ),
  modelYears: bindToPath(({ vehicle }) => buildModelFamilyIdsModelYearsPath(vehicle.modelFamilyIds), VehicleModel),
  forSaleYears: bindToPath(
    ({ vehicle }) => buildForSaleYearsPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
    VehicleModel
  ),
});
